import { useState } from "react";
import api, { extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  PenLine, X, RotateCcw, ChevronDown, CheckCircle2, Sparkles,
} from "lucide-react";
import { formatQidRefs } from "@/lib/qidFormat";
import { VoiceInputButton, appendTranscript } from "@/components/VoiceInputButton";

const CONF_LABEL = {
  stronger: "Stronger evidence",
  moderate: "Moderate evidence",
  limited: "Limited evidence",
};

const CONF_CLASS = {
  stronger: "bg-progress-teal/10 text-progress-teal border-progress-teal/40",
  moderate: "bg-accent-warm/10 text-accent-warm border-accent-warm/40",
  limited: "bg-bg-surface-hover text-fg-secondary border-border-subtle",
};

const EVIDENCE_LABEL = {
  direct: "You told us directly",
  stated_preference: "You stated this preference",
  inference: "Draft AI inference",
};

/**
 * Show a single conclusion. AI ones (`is_ai_generated`) can be edited or rejected.
 * Deterministic ones cannot (they mirror what the user typed / picked).
 *
 * Props:
 *   c       — the conclusion dict (id, section, title, body, evidence_type,
 *             confidence, supporting_answers, corrected_title, corrected_body,
 *             status, rejected_reason, is_ai_generated)
 *   answersById — {qid: {answer, skipped}} for the expander
 *   questionMap — {qid: {prompt, section, type, ...}} from /assessment/definitions
 *   onChanged   — (profile) => void  called after edit/reject/restore
 */
export function ConclusionCard({ c, answersById, questionMap, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(c.corrected_title ?? c.title);
  const [body, setBody] = useState(c.corrected_body ?? c.body);
  const [rejecting, setRejecting] = useState(false);
  const [rejReason, setRejReason] = useState(c.rejected_reason ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const displayTitle = c.corrected_title || c.title;
  const displayBody = c.corrected_body || c.body;
  const isCorrected = !!c.corrected_title || !!c.corrected_body;
  const isRejected = c.status === "rejected";

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.put(`/profile/conclusions/${c.id}`, {
        corrected_title: title.trim() || null,
        corrected_body: body.trim() || null,
      });
      setEditing(false);
      onChanged?.(data.profile);
      toast.success("Your correction is saved.");
    } catch (err) {
      setError(extractApiError(err, "Save failed."));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/profile/conclusions/${c.id}/reject`, {
        reason: rejReason.trim() || null,
      });
      setRejecting(false);
      onChanged?.(data.profile);
      toast.success("Rejected. Hidden from your profile.");
    } catch (err) {
      setError(extractApiError(err, "Reject failed."));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/profile/conclusions/${c.id}/restore`);
      onChanged?.(data.profile);
      toast.success("Restored to your profile.");
    } catch (err) {
      setError(extractApiError(err, "Restore failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`conclusion-${c.id}`}
      data-section={c.section}
      data-status={c.status}
      className={
        "rounded-lg border border-border-subtle bg-bg-surface p-5 " +
        (isRejected ? "opacity-60" : "")
      }
    >
      <div className="flex flex-wrap items-start gap-2 mb-3">
        <span
          data-testid={`conclusion-${c.id}-confidence`}
          className={"text-[11px] uppercase tracking-wider px-2 py-1 rounded border " + (CONF_CLASS[c.confidence] || "")}
        >
          {CONF_LABEL[c.confidence] || c.confidence}
        </span>
        <span
          data-testid={`conclusion-${c.id}-evidence`}
          className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-border-subtle text-fg-secondary bg-bg-base"
        >
          {EVIDENCE_LABEL[c.evidence_type] || c.evidence_type}
        </span>
        {c.is_ai_generated && (
          <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-accent-warm/40 text-accent-warm bg-accent-warm/10">
            Draft — you can correct or reject
          </span>
        )}
        {isCorrected && !isRejected && (
          <span data-testid={`conclusion-${c.id}-corrected-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-progress-teal/40 text-progress-teal bg-progress-teal/10">
            <CheckCircle2 className="h-3 w-3 mr-1 inline" aria-hidden="true" />
            Your correction
          </span>
        )}
        {isRejected && (
          <span data-testid={`conclusion-${c.id}-rejected-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-destructive/40 text-destructive bg-destructive/10">
            You rejected this
          </span>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-fg-secondary">
              Title
            </label>
            <input
              data-testid={`conclusion-${c.id}-title-input`}
              className="w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-fg-primary"
              value={title} maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-fg-secondary">
              Body
            </label>
            <Textarea
              data-testid={`conclusion-${c.id}-body-input`}
              className="bg-bg-base border-border-subtle text-fg-primary min-h-24"
              value={body} maxLength={4000}
              onChange={(e) => setBody(e.target.value)}
            />
            <div>
              <VoiceInputButton
                fieldTestid={`conclusion-${c.id}-body-voice-btn`}
                ariaLabel="Speak your correction"
                onTranscript={(t) => setBody(appendTranscript(body, t))}
              />
            </div>
          </div>
          {error && (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          )}
          <div className="flex gap-2">
            <Button
              size="sm" onClick={save} disabled={busy}
              data-testid={`conclusion-${c.id}-save-btn`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {busy ? "Saving…" : "Save my correction"}
            </Button>
            <Button
              size="sm" variant="ghost" onClick={() => { setEditing(false); setTitle(c.corrected_title ?? c.title); setBody(c.corrected_body ?? c.body); }}
              data-testid={`conclusion-${c.id}-cancel-btn`}
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <h3 className="text-lg font-heading font-medium text-fg-primary" data-testid={`conclusion-${c.id}-title`}>
            {formatQidRefs(displayTitle)}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-fg-secondary" data-testid={`conclusion-${c.id}-body`}>
            {formatQidRefs(displayBody)}
          </p>
          {isCorrected && (
            <details className="mt-2" data-testid={`conclusion-${c.id}-original-details`}>
              <summary className="text-xs text-fg-muted cursor-pointer">Original AI draft</summary>
              <div className="mt-2 pl-3 border-l border-border-subtle text-xs text-fg-muted">
                <p className="font-medium">{formatQidRefs(c.title)}</p>
                <p className="mt-1">{formatQidRefs(c.body)}</p>
              </div>
            </details>
          )}
          {c.rejected_reason && (
            <p className="mt-2 text-xs text-fg-muted italic">
              Your reason for rejecting: {c.rejected_reason}
            </p>
          )}
        </>
      )}

      {/* Based on your answers */}
      {c.supporting_answers?.length > 0 && (
        <Collapsible className="mt-4">
          <CollapsibleTrigger asChild>
            <button
              data-testid={`conclusion-${c.id}-expand-evidence`}
              className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg-primary transition-colors"
            >
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
              Based on your answers ({c.supporting_answers.length})
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <ul className="space-y-2 pl-2 border-l border-border-subtle">
              {c.supporting_answers.map((qid) => {
                const q = questionMap?.[qid];
                const a = answersById?.[qid];
                const f = /^s(\d{1,3})_q(\d{1,3})$/i.exec(String(qid).trim());
                const secN = f ? parseInt(f[1], 10) : null;
                const qN = f ? parseInt(f[2], 10) : null;
                return (
                  <li key={qid} data-testid={`conclusion-${c.id}-evidence-${qid}`} className="text-xs text-fg-secondary pl-3">
                    <div className="flex flex-wrap items-baseline gap-2">
                      {f && (
                        <span
                          data-testid={`conclusion-${c.id}-evidence-${qid}-ref`}
                          data-qid={qid}
                          aria-label={`Section ${secN}, Question ${qN}`}
                          className="inline-block rounded border border-border-subtle bg-bg-base px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wide text-fg-primary whitespace-nowrap"
                        >
                          {`S${secN}_Q${qN}`}
                          <span className="sr-only"> (Section {secN}, Question {qN})</span>
                        </span>
                      )}
                      <p className="text-fg-primary">{q?.prompt || qid}</p>
                    </div>
                    <p className="mt-1 italic">
                      {renderAnswerShort(q, a?.answer, a?.skipped)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Action row: only for AI-generated conclusions */}
      {c.is_ai_generated && !editing && (
        <div className="mt-4 pt-3 border-t border-border-subtle flex flex-wrap gap-2">
          {!isRejected && (
            <>
              <Button
                size="sm" variant="ghost"
                onClick={() => setEditing(true)}
                data-testid={`conclusion-${c.id}-correct-btn`}
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                <PenLine className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                {isCorrected ? "Edit correction" : "Correct this"}
              </Button>
              {!rejecting ? (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setRejecting(true)}
                  data-testid={`conclusion-${c.id}-reject-btn`}
                  className="text-fg-secondary hover:text-destructive hover:bg-destructive/10"
                >
                  <X className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                  Reject
                </Button>
              ) : (
                <div className="flex-1 flex flex-col gap-2 w-full min-w-full">
                  <Textarea
                    data-testid={`conclusion-${c.id}-reject-reason`}
                    className="bg-bg-base border-border-subtle text-fg-primary text-sm"
                    placeholder="Optional: why doesn't this fit? (max 400 chars)"
                    maxLength={400}
                    value={rejReason}
                    onChange={(e) => setRejReason(e.target.value)}
                  />
                  <VoiceInputButton
                    fieldTestid={`conclusion-${c.id}-reject-voice-btn`}
                    ariaLabel="Speak your reason"
                    onTranscript={(t) => setRejReason(appendTranscript(rejReason, t))}
                  />
                  {error && (
                    <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm" variant="destructive"
                      onClick={reject} disabled={busy}
                      data-testid={`conclusion-${c.id}-reject-confirm`}
                    >
                      {busy ? "Rejecting…" : "Confirm reject"}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => setRejecting(false)}
                      data-testid={`conclusion-${c.id}-reject-cancel`}
                      className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                    >Cancel</Button>
                  </div>
                </div>
              )}
            </>
          )}
          {isRejected && (
            <Button
              size="sm" variant="outline"
              onClick={restore} disabled={busy}
              data-testid={`conclusion-${c.id}-restore-btn`}
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Restore
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function renderAnswerShort(question, answer, skipped) {
  if (skipped) return "Marked skipped.";
  if (answer === null || answer === undefined || answer === "") return "Not answered.";
  if (!question) return String(answer);
  if (question.type === "text") {
    const s = String(answer);
    return s.length > 240 ? s.slice(0, 240) + "…" : s;
  }
  if (question.type === "single") {
    const o = (question.options || []).find((x) => x.value === answer);
    return o?.label || String(answer);
  }
  if (question.type === "multi") {
    if (!Array.isArray(answer)) return String(answer);
    const labels = answer.map((v) => (question.options || []).find((o) => o.value === v)?.label || v);
    return labels.join(" · ");
  }
  if (question.type === "scale") {
    return `${answer} of ${question.scale_max}`;
  }
  if (question.type === "strengths_with_examples") {
    if (!Array.isArray(answer)) return String(answer);
    return answer.map((s) => s.example ? `${s.strength.replace(/_/g, " ")} — “${s.example}”` : `${s.strength.replace(/_/g, " ")} (no example)`).join(" · ");
  }
  return JSON.stringify(answer);
}

export default ConclusionCard;
